// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — VIEW+SPACE domain (CC-2c sub-arm 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Translates the view+space AI command shapes (CREATE_VIEWPORT / MSPACE / PSPACE
// / SET_LAYOUT / RENAME_LAYOUT) into the internal `EditorCommand`s the store
// dispatches. Every
// path runs the CC-2a fail-closed guard (`assertAiCallableViewSpaceAction`)
// BEFORE producing any EditorCommand, so the safe contour rejects a
// non-AI-callable / unknown target without mutating the document. Layout /
// viewport / context state is only ever changed by the reducer the returned
// commands flow into — never written here — honouring the CLAUDE.md rules:
//   "Model/Layout/Viewport — это часть модели, а не только UI." +
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур."
//
// The thin per-action translators live in the PACKAGE-INTERNAL sibling
// `viewSpaceTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// view+space entries are the guarded `translateViewSpaceCommand` /
// `translateViewSpaceCommandBatch` below. This mirrors the BLOCK domain
// (per-action translators in non-barrelled `blockDispatcher*.ts`), the
// ENTITY-CREATION domain (`entityTranslators.ts`) and the DRAW-MODIFY domain
// (`drawModifyTranslators.ts`), preventing a consumer of
// `@dxf-ai-editor/ai-commands` from emitting a view/space command past the
// fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import {
  advanceDispatchContext,
  normalizeDispatcherArgs,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import { assertAiCallableViewSpaceAction } from "./viewSpaceGuard";
import type { ViewSpaceAiCommand } from "./viewSpaceSchemas";
import {
  translateCreateViewport,
  translateRenameLayout,
  translateSetCurrentColor,
  translateSetLayout,
  translateSwitchToModelSpace,
  translateSwitchToPaperSpace,
} from "./viewSpaceTranslators";

// Re-export the view+space safe-contour guard (mirrors drawModifyDispatcher
// re-exporting the draw guard) so consumers can reach it from this domain entry
// point — the raw per-action translators stay package-internal.
export {
  VIEW_SPACE_AI_ACTION_TARGET_COMMAND,
  ViewSpaceContextError,
  AiColorValidationError,
  assertAiCallableViewSpaceAction,
  resolveAiCurrentColor,
} from "./viewSpaceGuard";

// Back-compat / footgun guard for the descriptor argument (#287-13): a legacy
// `(command, descriptorArray)` call would treat the array as `context`, falling
// back to ALL_COMMAND_DESCRIPTORS and re-enabling a disabled command. Shared
// one-truth `normalizeDispatcherArgs` (in blockDispatcherShared) detects an array
// 2nd arg and re-routes it to `descriptors` so the call stays fail-closed.

/**
 * Top-level view+space dispatcher. Runs the CC-2a fail-closed guard, then routes
 * the AI command to the translator that owns its `action`. Returns the flat
 * `EditorCommand[]` the store should dispatch (the caller wraps the batch in one
 * grouped-undo unit — see the app safe contour). `context` carries the document
 * layout state the MSPACE/PSPACE/MVIEW translators need (active space + the
 * paper-space layout list); `descriptors` is injectable for tests / alternate
 * registries. Signature mirrors the entity / base-prim dispatchers
 * `(command, context, descriptors)`.
 */
export function translateViewSpaceCommand(
  command: ViewSpaceAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  ({ context, descriptors } = normalizeDispatcherArgs(context, descriptors));
  // Safe-contour gate (CC-2a): refuse to translate any view+space action whose
  // target command is not `aiCallable: true`. Fails closed before any
  // EditorCommand is produced.
  assertAiCallableViewSpaceAction(command.action, descriptors);
  switch (command.action) {
    case "CREATE_VIEWPORT":
      return translateCreateViewport(command, context);
    case "SWITCH_TO_MODEL_SPACE":
      return translateSwitchToModelSpace(command, context);
    case "SWITCH_TO_PAPER_SPACE":
      return translateSwitchToPaperSpace(command, context);
    case "SET_LAYOUT":
      return translateSetLayout(command, context);
    case "RENAME_LAYOUT":
      return translateRenameLayout(command);
    case "SET_CURRENT_COLOR":
      return translateSetCurrentColor(command);
  }
}

/**
 * Batch variant — flatten-translates a list of view+space AI commands in order
 * via {@link translateViewSpaceCommand} (so EACH command is guarded
 * individually). A non-AI-callable target anywhere in the batch throws before
 * any command is dispatched.
 */
export function translateViewSpaceCommandBatch(
  commands: readonly ViewSpaceAiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  ({ context, descriptors } = normalizeDispatcherArgs(context, descriptors));
  // Thread the running context (`advanceDispatchContext`) so a SET_LAYOUT /
  // MSPACE / PSPACE earlier in the batch updates the active space a LATER
  // MSPACE/PSPACE resolves against — mirrors `translateAiCommandBatch` so the
  // direct view+space batch entry and the generic seam share one ordering rule
  // (#290-16).
  const all: EditorCommand[] = [];
  let runningContext = context;
  for (const command of commands) {
    const emitted = translateViewSpaceCommand(
      command,
      runningContext,
      descriptors,
    );
    all.push(...emitted);
    runningContext = advanceDispatchContext(runningContext, emitted);
  }
  return all;
}
